'use client'

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import Link from 'next/link'
import './real-estate-tracker-case-study.css'

/**
 * Real Estate Earnings Tracker case study.
 *
 * A port of `public/radar-branding/real-estate-tracker-new.html`. That file is
 * the design contract: markup, class names, copy and section order match it, so
 * the two can be diffed. Styling lives in the sibling
 * `real-estate-tracker-case-study.css`, scoped under `.ret-cs`.
 *
 * Renders full-bleed -- `WorkPage`'s hero and container are skipped for this
 * slug (see `fullBleedProjects` in ./index.ts) because it ships its own hero.
 *
 * The source drove the dashboard with an IIFE that rebuilt DOM strings on every
 * interaction. Here the same arithmetic is pure and the output is rendered from
 * state, so the tape, table, chart, bridge and KPIs are all derived values
 * rather than innerHTML writes. The numbers themselves are unchanged.
 *
 * Every figure on this page is synthetic. The source labels it throughout
 * ("mock", "illustrative", "not investment, tax, or legal advice") and that
 * copy is carried over verbatim -- it is what keeps the demo honest.
 *
 * Deliberate departures from the source:
 *   1. Its own header, footer, contact strip and closing CTA are dropped: the
 *      site chrome already renders all four, and the last two would duplicate
 *      the layout's own bands.
 *   2. The "Case study status" section is dropped at Lloyd's request.
 *   3. The hero CTA pointed at a local file path; it is a same-page anchor.
 */

interface Property {
  id: string
  code: string
  name: string
  market: string
  value: number
  loan: number
  rent: number
  occupancy: number
  opex: number
  debt: number
}

/** Synthetic portfolio. Six invented Philippine properties -- not client data. */
const PROPERTIES: Property[] = [
  {
    id: 'salcedo',
    code: 'MNL-01',
    name: 'Salcedo 1BR',
    market: 'Makati · Residential',
    value: 13800000,
    loan: 7200000,
    rent: 83000,
    occupancy: 1,
    opex: 18700,
    debt: 44800,
  },
  {
    id: 'bgc',
    code: 'MNL-02',
    name: 'BGC Studio',
    market: 'Taguig · Residential',
    value: 9600000,
    loan: 5100000,
    rent: 56000,
    occupancy: 0.92,
    opex: 14200,
    debt: 32500,
  },
  {
    id: 'arca',
    code: 'MNL-03',
    name: 'Arca South 2BR',
    market: 'Taguig · Residential',
    value: 15400000,
    loan: 8400000,
    rent: 92000,
    occupancy: 1,
    opex: 22400,
    debt: 52200,
  },
  {
    id: 'rockwell',
    code: 'MNL-04',
    name: 'Rockwell Loft',
    market: 'Makati · Residential',
    value: 18200000,
    loan: 9700000,
    rent: 112000,
    occupancy: 0.96,
    opex: 28500,
    debt: 60500,
  },
  {
    id: 'cebu',
    code: 'CEB-01',
    name: 'Cebu IT Park 1BR',
    market: 'Cebu · Residential',
    value: 7800000,
    loan: 3600000,
    rent: 44000,
    occupancy: 0.88,
    opex: 11800,
    debt: 23600,
  },
  {
    id: 'ortigas',
    code: 'MNL-05',
    name: 'Ortigas 1BR',
    market: 'Pasig · Residential',
    value: 8900000,
    loan: 4200000,
    rent: 49000,
    occupancy: 0.94,
    opex: 12600,
    debt: 27000,
  },
]

interface Preset {
  occupancy: number
  rent: number
  opex: number
  growth: number
  label: string
}

const PRESETS: Record<string, Preset> = {
  base: { occupancy: 0, rent: 0, opex: 0, growth: 0.003, label: 'Base' },
  upside: { occupancy: 3, rent: 5, opex: 2, growth: 0.006, label: 'Upside' },
  downside: {
    occupancy: -8,
    rent: -4,
    opex: 8,
    growth: -0.002,
    label: 'Downside',
  },
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

/** Reproduces the source's formatter exactly, minus sign and all. */
const money = (value: number) => {
  const sign = value < 0 ? '−' : ''
  const abs = Math.abs(value)
  if (abs >= 1000000)
    return `${sign}₱${(abs / 1000000).toFixed(abs >= 10000000 ? 1 : 2).replace(/\.0$/, '')}M`
  if (abs >= 1000)
    return `${sign}₱${(abs / 1000).toFixed(abs >= 100000 ? 1 : 2).replace(/\.0$/, '')}K`
  return `${sign}₱${Math.round(abs).toLocaleString('en-PH')}`
}

const percent = (value: number) =>
  `${(value * 100).toFixed(value < 0.1 ? 2 : 1)}%`

interface Calculated extends Property {
  scheduled: number
  effective: number
  noi: number
  cash: number
  cap: number
  dscr: number
  ltv: number
}

function calculate(
  property: Property,
  preset: Preset,
  occAdjustment: number,
  rentAdjustment: number
): Calculated {
  const rentFactor = 1 + rentAdjustment / 100
  const expenseFactor = 1 + preset.opex / 100
  const scheduled = property.rent * rentFactor
  const occupancy = clamp(property.occupancy + occAdjustment / 100, 0.5, 1)
  const effective = scheduled * occupancy
  const opex = property.opex * expenseFactor
  const noi = effective - opex
  const cash = noi - property.debt
  return {
    ...property,
    scheduled,
    occupancy,
    effective,
    opex,
    noi,
    cash,
    cap: (noi * 12) / property.value,
    dscr: noi / property.debt,
    ltv: property.loan / property.value,
  }
}

type Focus = Omit<Calculated, 'id' | 'code'> & { id?: string; code?: string }

function aggregate(items: Calculated[]): Focus {
  const totals = items.reduce(
    (acc, item) => {
      for (const key of [
        'value',
        'loan',
        'scheduled',
        'effective',
        'opex',
        'noi',
        'debt',
        'cash',
      ] as const) {
        acc[key] += item[key]
      }
      return acc
    },
    {
      value: 0,
      loan: 0,
      scheduled: 0,
      effective: 0,
      opex: 0,
      noi: 0,
      debt: 0,
      cash: 0,
    }
  )
  return {
    ...totals,
    rent: totals.scheduled,
    name: 'All mock properties',
    market: '6-property portfolio',
    occupancy: totals.effective / totals.scheduled,
    cap: (totals.noi * 12) / totals.value,
    dscr: totals.noi / totals.debt,
    ltv: totals.loan / totals.value,
  }
}

const statusFor = (item: { dscr: number; cash: number; occupancy: number }) => {
  if (item.dscr < 1.1 || item.cash < 0)
    return { label: 'Pressure', className: 'ret-status-pressure' }
  if (item.occupancy < 0.9 || item.dscr < 1.25)
    return { label: 'Review', className: 'ret-status-review' }
  return { label: 'Stable', className: 'ret-status-stable' }
}

/** Four observed months then a trend, on the source's fixed seasonal curve. */
function forecastValues(focus: Focus, preset: Preset, horizon: number) {
  const seasonal = [
    0.92, 0.98, 0.95, 1, 1.01, 1.025, 1.02, 1.04, 1.03, 1.055, 1.05, 1.07,
  ]
  return Array.from({ length: horizon }, (_, index) => {
    if (index < 4) return focus.cash * seasonal[index]
    const phase = index % 12
    const trend = Math.pow(1 + preset.growth, index - 3)
    return focus.cash * trend * seasonal[phase]
  })
}

export default function RealEstateTrackerProjectContent() {
  const [selected, setSelected] = useState('portfolio')
  const [scenarioKey, setScenarioKey] = useState('base')
  const [horizon, setHorizon] = useState(12)
  const [occAdjustment, setOccAdjustment] = useState(0)
  const [rentAdjustment, setRentAdjustment] = useState(0)
  const [sortKey, setSortKey] = useState<
    'name' | 'value' | 'occupancy' | 'noi' | 'cap' | 'dscr' | 'cash'
  >('name')
  const [sortDirection, setSortDirection] = useState(1)
  const [copyLabel, setCopyLabel] = useState('Copy mock summary')

  // Moving a slider puts the model in "Custom", which is Base's opex and growth
  // with the slider values -- the same synthesized preset the source built.
  // Memoized because it is a dependency of every derived value below; rebuilt
  // fresh each render, it would defeat all of them.
  const preset: Preset = useMemo(
    () =>
      scenarioKey in PRESETS
        ? PRESETS[scenarioKey]
        : {
            ...PRESETS.base,
            occupancy: occAdjustment,
            rent: rentAdjustment,
            label: 'Custom',
          },
    [scenarioKey, occAdjustment, rentAdjustment]
  )

  const calculated = useMemo(
    () =>
      PROPERTIES.map(p => calculate(p, preset, occAdjustment, rentAdjustment)),
    [preset, occAdjustment, rentAdjustment]
  )

  const focus: Focus =
    selected === 'portfolio'
      ? aggregate(calculated)
      : (calculated.find(item => item.id === selected) ?? aggregate(calculated))

  const chooseScenario = (key: string) => {
    setScenarioKey(key)
    setOccAdjustment(PRESETS[key].occupancy)
    setRentAdjustment(PRESETS[key].rent)
  }

  const sorted = useMemo(() => {
    return [...calculated].sort((a, b) => {
      const va = sortKey === 'name' ? a.name : a[sortKey]
      const vb = sortKey === 'name' ? b.name : b[sortKey]
      return (
        (typeof va === 'string'
          ? va.localeCompare(vb as string)
          : (va as number) - (vb as number)) * sortDirection
      )
    })
  }, [calculated, sortKey, sortDirection])

  const toggleSort = (key: typeof sortKey) => {
    setSortDirection(sortKey === key ? sortDirection * -1 : 1)
    setSortKey(key)
  }

  // ── chart geometry, unchanged from the source ────────────────────────────
  const chart = useMemo(() => {
    const values = forecastValues(focus, preset, horizon)
    const actualCount = Math.min(4, values.length)
    let min = Math.min(0, ...values)
    let max = Math.max(0, ...values)
    const spread = Math.max(1, max - min)
    min -= spread * 0.12
    max += spread * 0.12
    const x = (i: number) => 48 + (i / (values.length - 1)) * 642
    const y = (value: number) => 228 - ((value - min) / (max - min)) * 180
    const points = values.map((value, index) => [x(index), y(value)] as const)
    const path = (arr: readonly (readonly [number, number])[]) =>
      arr
        .map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
        .join(' ')
    const last = points[points.length - 1]
    return {
      values,
      actualCount,
      min,
      max,
      points,
      actual: path(points.slice(0, actualCount)),
      forecast: path(points.slice(actualCount - 1)),
      area: `${path(points)} L${last[0].toFixed(1)},228 L${points[0][0].toFixed(1)},228 Z`,
      ruleX: points[actualCount - 1][0],
    }
  }, [focus, preset, horizon])

  // ── income bridge ────────────────────────────────────────────────────────
  const bridge = useMemo(() => {
    const vacancy = focus.scheduled - focus.effective
    const scale = Math.max(1, focus.scheduled)
    const size = (value: number) =>
      `${clamp((Math.abs(value) / scale) * 100, 2, 100)}%`
    return { vacancy, scale, size }
  }, [focus])

  const copySummary = async () => {
    const summary = `MOCK DATA: ${focus.name}: value ${money(focus.value)}, monthly NOI ${money(focus.noi)}, monthly cash flow ${money(focus.cash)}, cap rate ${percent(focus.cap)}, DSCR ${focus.dscr.toFixed(2)}×, occupancy ${percent(focus.occupancy)}. Illustrative only; not investment advice.`
    try {
      await navigator.clipboard.writeText(summary)
      setCopyLabel('Mock summary copied')
    } catch (error) {
      console.error('Clipboard write failed', error)
      setCopyLabel('Copy unavailable')
    }
    setTimeout(() => setCopyLabel('Copy mock summary'), 1800)
  }

  const scopeLabel = `${focus.name} · ${preset.label} · ${horizon} months`

  return (
    <div className='ret-cs'>
      <section className='ret-hero' aria-labelledby='page-title'>
        <div className='ret-wrap ret-hero-grid'>
          <div className='ret-hero-copy'>
            <div className='ret-badges'>
              <span className='ret-badge'>Real Estate Tech</span>
              <span className='ret-badge'>2025</span>
            </div>
            <h1 className='ret-display' id='page-title'>
              Real Estate <span className='ret-accent'>Earnings Tracker</span>
            </h1>
            <p className='ret-hero-lead'>
              Predictive analytics platform that transforms property investment
              data into actionable profit forecasts with real-time market
              analysis and portfolio optimization.
            </p>
            <div className='ret-hero-actions'>
              <a className='ret-btn ret-btn-yellow' href='#dashboard-demo'>
                Explore demo dashboard <span aria-hidden='true'>↓</span>
              </a>
              <Link className='ret-btn ret-btn-outline' href='/projects'>
                View all projects <span aria-hidden='true'>↗</span>
              </Link>
            </div>
            <div className='ret-hero-stack'>
              <p className='ret-label'>Technologies:</p>
              <div className='ret-tech-list' aria-label='Technologies used'>
                <span className='ret-tech'>React</span>
                <span className='ret-tech'>TypeScript</span>
                <span className='ret-tech'>Python</span>
                <span className='ret-tech'>Machine Learning</span>
                <span className='ret-tech'>PostgreSQL</span>
                <span className='ret-tech'>Chart.js</span>
              </div>
            </div>
          </div>
          <figure className='ret-hero-art ret-hero-infographic'>
            <div className='ret-art-index'>
              <span>Portfolio anatomy · 01</span>
              <span>Mock property earnings</span>
            </div>
            <div className='ret-ig-shell'>
              <header className='ret-ig-head'>
                <div>
                  <p className='ret-eyebrow'>Illustrative portfolio data</p>
                  <h2>Where the earnings go.</h2>
                </div>
                <span className='ret-ig-mode'>Scenario · Base</span>
              </header>
              <div className='ret-ig-visual'>
                <svg
                  viewBox='0 0 620 455'
                  role='img'
                  aria-labelledby='ig-title ig-desc'
                >
                  <title id='ig-title'>
                    Mock property portfolio value and monthly earnings
                    infographic
                  </title>
                  <desc id='ig-desc'>
                    Six synthetic Philippine properties shown as value towers. A
                    monthly rent strip separates vacancy, operating cost, debt
                    service, and remaining cash flow. All values are
                    illustrative.
                  </desc>
                  <defs>
                    <pattern
                      id='ig-hatch'
                      width='6'
                      height='6'
                      patternUnits='userSpaceOnUse'
                      patternTransform='rotate(45)'
                    >
                      <line
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='6'
                        stroke='#ffffff'
                        strokeOpacity='.32'
                        strokeWidth='2'
                      />
                    </pattern>
                    <linearGradient id='ig-pink' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#d9097a' />
                      <stop offset='1' stopColor='#ff5aaf' />
                    </linearGradient>
                    <linearGradient id='ig-red' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#f14635' />
                      <stop offset='1' stopColor='#ff786b' />
                    </linearGradient>
                    <linearGradient id='ig-yellow' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#d99e00' />
                      <stop offset='1' stopColor='#fdc304' />
                    </linearGradient>
                    <linearGradient id='ig-green' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#138c69' />
                      <stop offset='1' stopColor='#5dd4af' />
                    </linearGradient>
                    <linearGradient id='ig-cyan' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#008dcc' />
                      <stop offset='1' stopColor='#74d5f4' />
                    </linearGradient>
                    <linearGradient id='ig-purple' x1='0' y1='1' x2='0' y2='0'>
                      <stop stopColor='#694dcc' />
                      <stop offset='1' stopColor='#a28cff' />
                    </linearGradient>
                  </defs>

                  <g aria-hidden='true'>
                    <text className='ret-ig-axis' x='8' y='58'>
                      MOCK VALUE
                    </text>
                    <text className='ret-ig-axis' x='10' y='89'>
                      ₱20M
                    </text>
                    <line
                      className='ret-ig-rule'
                      x1='52'
                      y1='85'
                      x2='596'
                      y2='85'
                    />
                    <text className='ret-ig-axis' x='10' y='197'>
                      ₱10M
                    </text>
                    <line
                      className='ret-ig-rule'
                      x1='52'
                      y1='193'
                      x2='596'
                      y2='193'
                    />
                    <text className='ret-ig-axis' x='22' y='311'>
                      ₱0
                    </text>
                    <line
                      className='ret-ig-baseline'
                      x1='52'
                      y1='307'
                      x2='596'
                      y2='307'
                    />

                    <g style={{ '--delay': '.05s' } as CSSProperties}>
                      <text className='ret-ig-value' x='91' y='126'>
                        ₱13.8M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='61'
                        y='135'
                        width='60'
                        height='172'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='61'
                        y='135'
                        width='60'
                        height='172'
                        fill='url(#ig-pink)'
                      />
                      <text className='ret-ig-code' x='91' y='328'>
                        MNL-01
                      </text>
                      <text className='ret-ig-occ' x='91' y='343'>
                        100% OCC
                      </text>
                    </g>
                    <g style={{ '--delay': '.15s' } as CSSProperties}>
                      <text className='ret-ig-value' x='177' y='179'>
                        ₱9.6M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='147'
                        y='188'
                        width='60'
                        height='119'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='147'
                        y='198'
                        width='60'
                        height='109'
                        fill='url(#ig-red)'
                      />
                      <rect
                        className='ret-ig-cap'
                        x='147'
                        y='188'
                        width='60'
                        height='10'
                      />
                      <text className='ret-ig-code' x='177' y='328'>
                        MNL-02
                      </text>
                      <text className='ret-ig-occ' x='177' y='343'>
                        92% OCC
                      </text>
                    </g>
                    <g style={{ '--delay': '.25s' } as CSSProperties}>
                      <text className='ret-ig-value' x='263' y='106'>
                        ₱15.4M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='233'
                        y='115'
                        width='60'
                        height='192'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='233'
                        y='115'
                        width='60'
                        height='192'
                        fill='url(#ig-yellow)'
                      />
                      <text className='ret-ig-code' x='263' y='328'>
                        MNL-03
                      </text>
                      <text className='ret-ig-occ' x='263' y='343'>
                        100% OCC
                      </text>
                    </g>
                    <g style={{ '--delay': '.35s' } as CSSProperties}>
                      <text className='ret-ig-value' x='349' y='71'>
                        ₱18.2M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='319'
                        y='80'
                        width='60'
                        height='227'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='319'
                        y='89'
                        width='60'
                        height='218'
                        fill='url(#ig-green)'
                      />
                      <rect
                        className='ret-ig-cap'
                        x='319'
                        y='80'
                        width='60'
                        height='9'
                      />
                      <text className='ret-ig-code' x='349' y='328'>
                        MNL-04
                      </text>
                      <text className='ret-ig-occ' x='349' y='343'>
                        96% OCC
                      </text>
                    </g>
                    <g style={{ '--delay': '.45s' } as CSSProperties}>
                      <text className='ret-ig-value' x='435' y='201'>
                        ₱7.8M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='405'
                        y='210'
                        width='60'
                        height='97'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='405'
                        y='222'
                        width='60'
                        height='85'
                        fill='url(#ig-cyan)'
                      />
                      <rect
                        className='ret-ig-cap'
                        x='405'
                        y='210'
                        width='60'
                        height='12'
                      />
                      <text className='ret-ig-code' x='435' y='328'>
                        CEB-01
                      </text>
                      <text className='ret-ig-occ' x='435' y='343'>
                        88% OCC
                      </text>
                    </g>
                    <g style={{ '--delay': '.55s' } as CSSProperties}>
                      <text className='ret-ig-value' x='521' y='188'>
                        ₱8.9M
                      </text>
                      <rect
                        className='ret-ig-frame'
                        x='491'
                        y='197'
                        width='60'
                        height='110'
                      />
                      <rect
                        className='ret-ig-tower-fill'
                        x='491'
                        y='204'
                        width='60'
                        height='103'
                        fill='url(#ig-purple)'
                      />
                      <rect
                        className='ret-ig-cap'
                        x='491'
                        y='197'
                        width='60'
                        height='7'
                      />
                      <text className='ret-ig-code' x='521' y='328'>
                        MNL-05
                      </text>
                      <text className='ret-ig-occ' x='521' y='343'>
                        94% OCC
                      </text>
                    </g>

                    <text className='ret-ig-axis' x='61' y='374'>
                      MONTHLY SCHEDULED RENT · ₱436K
                    </text>
                    <rect
                      x='61'
                      y='387'
                      width='20'
                      height='18'
                      fill='#f90b8a'
                    />
                    <rect
                      x='81'
                      y='387'
                      width='124'
                      height='18'
                      fill='#fdc304'
                    />
                    <rect
                      x='205'
                      y='387'
                      width='276'
                      height='18'
                      fill='#7e64fc'
                    />
                    <rect
                      x='481'
                      y='387'
                      width='70'
                      height='18'
                      fill='#5dd4af'
                    />
                    <path
                      className='ret-ig-bracket'
                      d='M205 379 V369 H551 V379'
                    />
                    <text
                      className='ret-ig-axis'
                      x='378'
                      y='362'
                      textAnchor='middle'
                      fill='#5cb8e6'
                    >
                      NOI · ₱310.6K
                    </text>
                    <text className='ret-ig-flow-label' x='71' y='421'>
                      VACANCY
                    </text>
                    <text className='ret-ig-flow-value' x='71' y='435'>
                      −17.2
                    </text>
                    <text className='ret-ig-flow-label' x='143' y='421'>
                      OPERATING
                    </text>
                    <text className='ret-ig-flow-value' x='143' y='435'>
                      −108.2
                    </text>
                    <text className='ret-ig-flow-label' x='343' y='421'>
                      DEBT SERVICE
                    </text>
                    <text className='ret-ig-flow-value' x='343' y='435'>
                      −240.6
                    </text>
                    <text className='ret-ig-flow-label' x='516' y='421'>
                      CASH / MO
                    </text>
                    <text className='ret-ig-flow-value' x='516' y='435'>
                      70.0
                    </text>
                  </g>
                </svg>
              </div>
              <div
                className='ret-ig-metric-strip'
                aria-label='Mock portfolio ratios'
              >
                <div className='ret-ig-metric'>
                  <span>Portfolio value</span>
                  <strong>₱73.7M</strong>
                </div>
                <div className='ret-ig-metric'>
                  <span>Weighted occupancy</span>
                  <strong>96.1%</strong>
                </div>
                <div className='ret-ig-metric'>
                  <span>Coverage / DSCR</span>
                  <strong>1.29×</strong>
                </div>
              </div>
              <figcaption className='ret-ig-caption'>
                <span>
                  <strong>Demo · Synthetic data</strong>
                </span>
                <span>
                  Values mirror the mock dashboard below · Not investment advice
                </span>
              </figcaption>
            </div>
          </figure>
        </div>
      </section>

      <section
        className='ret-dashboard-section'
        id='dashboard-demo'
        aria-labelledby='dashboard-title'
      >
        <div className='ret-wrap'>
          <div className='ret-section-intro'>
            <div>
              <p className='ret-eyebrow'>
                Interactive product concept · Mock data
              </p>
              <h2 className='ret-display' id='dashboard-title'>
                One portfolio.
                <br />
                Every earnings signal.
              </h2>
            </div>
            <p>
              A redesigned demonstration of the tracker pattern: pick a
              synthetic asset, move the assumptions, and watch cash flow,
              coverage, and portfolio pressure update together. Every number is
              invented.
            </p>
          </div>

          <div className='ret-dashboard-shell'>
            <div className='ret-dash-topbar'>
              <div className='ret-dash-title'>
                <span className='ret-status-dot' aria-hidden='true' />
                <span>Earnings tracker / underwriting desk</span>
              </div>
              <div className='ret-dash-title ret-dash-warning'>
                Illustrative values only · No client results
              </div>
            </div>

            <div className='ret-dash-controls' aria-label='Dashboard controls'>
              <div className='ret-control-block'>
                <p className='ret-label'>01 · Scenario preset</p>
                <div className='ret-segmented'>
                  {(['base', 'upside', 'downside'] as const).map(key => (
                    <button
                      key={key}
                      className='ret-segment'
                      type='button'
                      aria-pressed={scenarioKey === key}
                      onClick={() => chooseScenario(key)}
                    >
                      {PRESETS[key].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className='ret-control-block ret-horizon'>
                <p className='ret-label'>02 · Forecast horizon</p>
                <div className='ret-segmented'>
                  {[12, 24, 36].map(months => (
                    <button
                      key={months}
                      className='ret-segment'
                      type='button'
                      aria-pressed={horizon === months}
                      onClick={() => setHorizon(months)}
                    >
                      {months} mo
                    </button>
                  ))}
                </div>
              </div>
              <div className='ret-control-block'>
                <p className='ret-label'>03 · Assumptions</p>
                <div className='ret-range-row'>
                  <label htmlFor='occupancy-adjustment'>
                    Occupancy adjustment
                  </label>
                  <output
                    className='ret-range-value'
                    htmlFor='occupancy-adjustment'
                  >
                    {occAdjustment > 0 ? '+' : ''}
                    {occAdjustment} pp
                  </output>
                </div>
                <input
                  id='occupancy-adjustment'
                  type='range'
                  min='-15'
                  max='8'
                  step='1'
                  value={occAdjustment}
                  onChange={e => {
                    setOccAdjustment(Number(e.target.value))
                    setScenarioKey('custom')
                  }}
                />
                <div className='ret-range-row'>
                  <label htmlFor='rent-adjustment'>Rent adjustment</label>
                  <output className='ret-range-value' htmlFor='rent-adjustment'>
                    {rentAdjustment > 0 ? '+' : ''}
                    {rentAdjustment}%
                  </output>
                </div>
                <input
                  id='rent-adjustment'
                  type='range'
                  min='-10'
                  max='12'
                  step='1'
                  value={rentAdjustment}
                  onChange={e => {
                    setRentAdjustment(Number(e.target.value))
                    setScenarioKey('custom')
                  }}
                />
              </div>
            </div>

            <div
              className='ret-kpi-ledger'
              aria-label='Selected portfolio metrics'
            >
              <div className='ret-kpi'>
                <p className='ret-label'>Asset value</p>
                <strong>{money(focus.value)}</strong>
                <small>Mock current value</small>
              </div>
              <div className='ret-kpi'>
                <p className='ret-label'>NOI / month</p>
                <strong>{money(focus.noi)}</strong>
                <small>Effective rent − OpEx</small>
              </div>
              <div
                className='ret-kpi'
                data-tone={focus.cash < 0 ? 'attention' : 'good'}
              >
                <p className='ret-label'>Cash / month</p>
                <strong>{money(focus.cash)}</strong>
                <small>After debt service</small>
              </div>
              <div className='ret-kpi'>
                <p className='ret-label'>Cap rate</p>
                <strong>{percent(focus.cap)}</strong>
                <small>Annual NOI / value</small>
              </div>
              <div className='ret-kpi'>
                <p className='ret-label'>DSCR</p>
                <strong>{focus.dscr.toFixed(2)}×</strong>
                <small>NOI / debt service</small>
              </div>
              <div className='ret-kpi'>
                <p className='ret-label'>Occupancy</p>
                <strong>{percent(focus.occupancy)}</strong>
                <small>Rent-weighted</small>
              </div>
            </div>

            <div className='ret-dashboard-main'>
              <section
                className='ret-forecast-panel'
                aria-labelledby='forecast-title'
              >
                <div className='ret-panel-head'>
                  <div>
                    <p className='ret-label'>Forecast trace</p>
                    <h3 id='forecast-title'>Monthly cash flow</h3>
                  </div>
                  <div className='ret-panel-meta'>{scopeLabel}</div>
                </div>
                <div className='ret-chart-wrap'>
                  <svg
                    viewBox='0 0 720 280'
                    role='img'
                    aria-label={`Illustrative ${horizon}-month cash-flow trace for ${focus.name}, starting near ${money(chart.values[0])} per month and ending near ${money(chart.values[chart.values.length - 1])} per month.`}
                  >
                    <defs>
                      <linearGradient
                        id='ret-chart-fill'
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop
                          offset='0'
                          stopColor='#5cb8e6'
                          stopOpacity='.32'
                        />
                        <stop offset='1' stopColor='#5cb8e6' stopOpacity='0' />
                      </linearGradient>
                    </defs>
                    <g aria-hidden='true'>
                      {[48, 108, 168, 228].map(yy => (
                        <line
                          key={yy}
                          className='ret-chart-grid'
                          x1='48'
                          y1={yy}
                          x2='690'
                          y2={yy}
                        />
                      ))}
                      <text className='ret-chart-axis' x='8' y='51'>
                        {money(chart.max)}
                      </text>
                      <text className='ret-chart-axis' x='8' y='171'>
                        {money((chart.max + chart.min) / 2)}
                      </text>
                      <text className='ret-chart-axis' x='8' y='231'>
                        {money(chart.min)}
                      </text>
                      <text className='ret-chart-axis' x='48' y='252'>
                        M01
                      </text>
                      <text className='ret-chart-axis' x='660' y='252'>
                        M{String(horizon).padStart(2, '0')}
                      </text>
                    </g>
                    <path
                      className='ret-chart-area'
                      d={chart.area}
                      fill='url(#ret-chart-fill)'
                    />
                    <line
                      className='ret-forecast-rule'
                      x1={chart.ruleX}
                      y1='40'
                      x2={chart.ruleX}
                      y2='236'
                    />
                    <path className='ret-chart-path-actual' d={chart.actual} />
                    <path
                      className='ret-chart-path-forecast'
                      d={chart.forecast}
                    />
                    <g>
                      {chart.points.map((point, index) => (
                        <circle
                          key={index}
                          className='ret-chart-marker'
                          cx={point[0].toFixed(1)}
                          cy={point[1].toFixed(1)}
                          r={index === chart.actualCount - 1 ? 4 : 2.6}
                        />
                      ))}
                    </g>
                  </svg>
                </div>
                <div className='ret-chart-legend'>
                  <span className='ret-chart-key'>Observed months</span>
                  <span className='ret-chart-key'>Forecast</span>
                </div>
              </section>

              <section
                className='ret-bridge-panel'
                aria-labelledby='bridge-title'
              >
                <div className='ret-panel-head'>
                  <div>
                    <p className='ret-label'>Income bridge</p>
                    <h3 id='bridge-title'>Rent to cash</h3>
                  </div>
                </div>
                <div className='ret-bridge-list'>
                  {[
                    {
                      key: 'scheduled',
                      label: 'Scheduled rent',
                      value: focus.scheduled,
                      deduction: false,
                    },
                    {
                      key: 'vacancy',
                      label: 'Vacancy loss',
                      value: bridge.vacancy,
                      deduction: true,
                    },
                    {
                      key: 'opex',
                      label: 'Operating cost',
                      value: focus.opex,
                      deduction: true,
                    },
                    {
                      key: 'noi',
                      label: 'NOI',
                      value: focus.noi,
                      deduction: false,
                    },
                    {
                      key: 'debt',
                      label: 'Debt service',
                      value: focus.debt,
                      deduction: true,
                    },
                    {
                      key: 'cash',
                      label: 'Cash flow',
                      value: focus.cash,
                      deduction: focus.cash < 0,
                      result: true,
                    },
                  ].map(row => (
                    <div
                      key={row.key}
                      className={`ret-bridge-item${row.deduction ? ' ret-negative' : ''}${row.result ? ' ret-result' : ''}${row.result && focus.cash < 0 ? ' ret-negative' : ''}`}
                    >
                      <span>{row.label}</span>
                      <span className='ret-bridge-track'>
                        <span
                          className='ret-bridge-fill'
                          style={
                            {
                              '--size': bridge.size(row.value),
                            } as CSSProperties
                          }
                        />
                      </span>
                      <strong>
                        {row.deduction ? '−' : ''}
                        {money(Math.abs(row.value))}
                      </strong>
                    </div>
                  ))}
                </div>
                <p className='ret-formula-note'>
                  Scheduled rent − vacancy − operating cost = NOI. NOI − debt
                  service = cash flow. All figures are invented for the demo.
                </p>
              </section>
            </div>

            <section
              className='ret-portfolio-tape'
              aria-labelledby='tape-title'
            >
              <div className='ret-tape-head'>
                <div>
                  <p className='ret-label'>Property tape</p>
                  <h3 id='tape-title'>Select a mock asset</h3>
                </div>
                <button
                  className='ret-segment'
                  type='button'
                  aria-pressed={selected === 'portfolio'}
                  onClick={() => setSelected('portfolio')}
                >
                  All properties
                </button>
              </div>
              <div className='ret-tape-grid'>
                {calculated.map(item => {
                  const status = statusFor(item)
                  const widths = {
                    vacancy: Math.max(0, 1 - item.occupancy) * 100,
                    opex: Math.max(0, (item.opex / item.scheduled) * 100),
                    debt: Math.max(0, (item.debt / item.scheduled) * 100),
                    cash: (Math.abs(item.cash) / item.scheduled) * 100,
                  }
                  return (
                    <button
                      key={item.id}
                      className='ret-asset-button'
                      type='button'
                      aria-pressed={selected === item.id}
                      onClick={() => setSelected(item.id)}
                    >
                      <span className='ret-asset-top'>
                        <span>
                          <span className='ret-asset-name'>{item.name}</span>
                          <span className='ret-asset-meta'>{item.market}</span>
                        </span>
                        <span className='ret-asset-code'>{item.code}</span>
                      </span>
                      <span className='ret-asset-bar' aria-hidden='true'>
                        <span
                          className='ret-vacancy'
                          style={{ width: `${widths.vacancy}%` }}
                        />
                        <span
                          className='ret-opex'
                          style={{ width: `${widths.opex}%` }}
                        />
                        <span
                          className='ret-debt'
                          style={{ width: `${widths.debt}%` }}
                        />
                        <span
                          className={
                            item.cash < 0 ? 'ret-pressure' : 'ret-cash'
                          }
                          style={{ width: `${widths.cash}%` }}
                        />
                      </span>
                      <span className='ret-asset-foot'>
                        <span>{percent(item.occupancy)} occupied</span>
                        <span className={status.className}>{status.label}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>

            <section className='ret-table-panel' aria-labelledby='table-title'>
              <div className='ret-table-tools'>
                <div>
                  <p className='ret-label'>Underwriting ledger</p>
                  <h3 id='table-title'>Property comparison</h3>
                </div>
                <button
                  className='ret-copy-button'
                  type='button'
                  onClick={copySummary}
                >
                  {copyLabel}
                </button>
              </div>
              <div className='ret-table-scroll'>
                <table>
                  <caption>
                    All properties, values, and forecasts are illustrative.
                    Choose a property name to focus the dashboard.
                  </caption>
                  <thead>
                    <tr>
                      {(
                        [
                          ['name', 'Property'],
                          ['value', 'Value'],
                          ['occupancy', 'Occupancy'],
                          ['noi', 'NOI / mo'],
                          ['cap', 'Cap rate'],
                          ['dscr', 'DSCR'],
                          ['cash', 'Cash / mo'],
                        ] as const
                      ).map(([key, label]) => (
                        <th key={key} scope='col'>
                          <button
                            className='ret-sort-button'
                            type='button'
                            aria-sort={
                              sortKey === key
                                ? sortDirection === 1
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                            onClick={() => toggleSort(key)}
                          >
                            {label} ↕
                          </button>
                        </th>
                      ))}
                      <th scope='col'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(item => {
                      const status = statusFor(item)
                      return (
                        <tr key={item.id} data-selected={selected === item.id}>
                          <td>
                            <button
                              className='ret-property-select'
                              type='button'
                              onClick={() => setSelected(item.id)}
                            >
                              {item.name}
                            </button>
                          </td>
                          <td>{money(item.value)}</td>
                          <td>{percent(item.occupancy)}</td>
                          <td>{money(item.noi)}</td>
                          <td>{percent(item.cap)}</td>
                          <td>{item.dscr.toFixed(2)}×</td>
                          <td>{money(item.cash)}</td>
                          <td>
                            <span
                              className={`ret-status-pill ${status.className}`}
                            >
                              {status.label}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <div className='ret-dashboard-foot'>
              <span>Selected: {focus.name}</span>
              <span>
                Demo · Synthetic data · Not investment, tax, or legal advice
              </span>
            </div>
            <p className='ret-sr-status' aria-live='polite'>
              {`${focus.name} selected. Mock monthly NOI ${money(focus.noi)}, cash flow ${money(focus.cash)}, cap rate ${percent(focus.cap)}, DSCR ${focus.dscr.toFixed(2)} times.`}
            </p>
          </div>
        </div>
      </section>

      <section
        className='ret-decision-section'
        aria-labelledby='decision-title'
      >
        <div className='ret-wrap'>
          <div className='ret-decision-head'>
            <div>
              <p className='ret-eyebrow'>The interface pattern</p>
              <h2 className='ret-display' id='decision-title'>
                From property data to a decision.
              </h2>
            </div>
            <p>
              The demo makes the source proposition tangible: organize property
              investment inputs, surface earnings pressure, model a forecast,
              and compare the portfolio without turning simulated numbers into
              case-study proof.
            </p>
          </div>
          <div className='ret-decision-path'>
            <article className='ret-decision-step'>
              <span className='ret-step-no'>01 / INPUT</span>
              <h3>Property tape</h3>
              <p>
                Value, debt, rent, occupancy, operating expense, and debt
                service sit in one consistent record.
              </p>
            </article>
            <article className='ret-decision-step'>
              <span className='ret-step-no'>02 / NORMALIZE</span>
              <h3>Earnings bridge</h3>
              <p>
                Every asset resolves to the same scheduled rent → NOI →
                cash-flow logic.
              </p>
            </article>
            <article className='ret-decision-step'>
              <span className='ret-step-no'>03 / FORECAST</span>
              <h3>Scenario trace</h3>
              <p>
                Base, upside, and downside assumptions expose how the
                illustrative forecast changes.
              </p>
            </article>
            <article className='ret-decision-step'>
              <span className='ret-step-no'>04 / DECIDE</span>
              <h3>Portfolio view</h3>
              <p>
                Compare cap rate, coverage, occupancy, and cash position before
                choosing what needs attention.
              </p>
            </article>
          </div>
        </div>
      </section>
    </div>
  )
}
